import { useId } from 'preact/hooks'
import { X } from 'lucide-preact'
import type { LandingPageTemplateListItemDto } from '@/types/landingPage'
import { TemplatePreviewPane } from './TemplatePreviewPane'
import { signedOffText } from './templateText'
import { useDialogFocus } from './useDialogFocus'

interface TemplatePreviewOverlayProps {
    workspaceId: string
    template: LandingPageTemplateListItemDto
    onClose: () => void
}

/** A template's pages at full size (ORB-1103), over everything, until Escape or the close button. */
export function TemplatePreviewOverlay({ workspaceId, template, onClose }: TemplatePreviewOverlayProps) {
    const titleId = useId()
    const { dialogRef, initialFocusRef } = useDialogFocus<HTMLButtonElement>(onClose)
    const version = template.LatestSignedOffVersion

    return (
        <div
            ref={dialogRef}
            role="dialog"
            aria-modal="true"
            aria-labelledby={titleId}
            tabIndex={-1}
            className="fixed inset-0 z-[60] flex flex-col bg-white dark:bg-slate-900 focus:outline-none"
        >
            <div className="shrink-0 flex items-center justify-between gap-4 px-4 py-2.5 border-b border-slate-200 dark:border-slate-700">
                <p className="min-w-0 flex items-baseline gap-2">
                    <span id={titleId} className="truncate text-sm font-medium text-slate-700 dark:text-slate-200">{template.Template.Name}</span>
                    {version && (
                        <span className="hidden sm:inline whitespace-nowrap text-xs text-slate-500 dark:text-slate-400">Uses {signedOffText(version)}</span>
                    )}
                </p>
                <button
                    ref={initialFocusRef}
                    type="button"
                    onClick={onClose}
                    aria-label="Close preview"
                    className="shrink-0 p-1.5 rounded-md text-slate-500 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800 hover:text-slate-700 dark:hover:text-slate-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500"
                >
                    <X className="w-5 h-5" aria-hidden="true" />
                </button>
            </div>
            <TemplatePreviewPane workspaceId={workspaceId} template={template} />
        </div>
    )
}
